import { getCurrentSession } from './apiAuth';

const API_BASE_URL = 'https://api-jurisai.polykloud.com/api';

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  document?: T;
  error?: string;
  message?: string;
}

export interface DocumentUploadResponse {
  id: string;
  name: string;
  filename: string;
  size: string;
  status: string;
  processingStatus: string;
  chunksGenerated: string;
  embeddingsGenerated: string;
  uploadDate: string;
  duplicate?: boolean;
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      // Get authentication token from session
      const session = getCurrentSession();
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...options.headers as Record<string, string>,
      };
      
      // Add Authorization header if user is authenticated
      if (session?.token) {
        headers['Authorization'] = `Bearer ${session.token}`;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers,
        ...options,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error?.message || `HTTP ${response.status}`,
        };
      }

      return data;
    } catch (error) {
      console.error('API request failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // Document upload
  async uploadDocument(file: File): Promise<ApiResponse<DocumentUploadResponse>> {
    const formData = new FormData();
    formData.append('document', file);

    // Get authentication token from session
    const session = getCurrentSession();
    const headers: Record<string, string> = {};
    
    // Add Authorization header if user is authenticated
    if (session?.token) {
      headers['Authorization'] = `Bearer ${session.token}`;
    }

    try {
      console.log('🌐 Sending request to:', `${API_BASE_URL}/documents/upload`);
      console.log('🔐 With auth token:', session?.token ? 'Yes' : 'No');
      
      const response = await fetch(`${API_BASE_URL}/documents/upload`, {
        method: 'POST',
        headers, // Include auth headers
        body: formData,
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to parse error response' }));
        console.error('❌ HTTP Error:', response.status, errorData);
        return {
          success: false,
          error: errorData.error?.message || `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      const data = await response.json();
      console.log('✅ Response data:', data);

      // Return the response exactly as the backend sends it
      return data;
    } catch (error) {
      console.error('❌ Network/Parse Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error - check CORS and backend connection',
      };
    }
  }

  // Generate smart prompts based on embeddings
  async generateSmartPrompts(documentIds: number[]): Promise<{
    success: boolean;
    prompts?: any[];
    documentIds?: number[];
    generatedAt?: string;
    error?: string;
  }> {
    return this.request('/prompts/generate', {
      method: 'POST',
      body: JSON.stringify({ documentIds }),
    });
  }

  // Get documents
  async getDocuments(): Promise<ApiResponse<any[]>> {
    return this.request('/documents');
  }

  // Health check
  async healthCheck(): Promise<ApiResponse> {
    try {
      const response = await fetch(`${API_BASE_URL.replace('/api', '')}/health`);
      const data = await response.json();
      
      return {
        success: response.ok,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: 'Health check failed',
      };
    }
  }

  // RAG Query (non-streaming)
  async askQuestion(
    question: string,
    documentId?: number,
    options?: {
      temperature?: number;
      maxTokens?: number;
      systemPrompt?: string;
    }
  ): Promise<ApiResponse<{
    answer: string;
    sources: any[];
    confidence: number;
    processingTime: number;
    tokenCount: number;
  }>> {
    return this.request('/queries/ask', {
      method: 'POST',
      body: JSON.stringify({
        question,
        documentId,
        options,
      }),
    });
  }

  // Get uploaded documents for RAG queries
  async getUploadedDocuments(): Promise<ApiResponse<any[]>> {
    const response = await this.request('/documents');
    
    // Backend returns {success: true, documents: [...]} but frontend expects {success: true, data: [...]}
    if (response.success && (response as any).documents) {
      return {
        success: true,
        data: (response as any).documents
      };
    }
    
    return response as ApiResponse<any[]>;
  }

  // Delete document
  async deleteDocument(id: string): Promise<ApiResponse> {
    return this.request(`/documents/${id}`, {
      method: 'DELETE',
    });
  }

  // Get document content (reconstructed from chunks)
  async getDocumentContent(documentId: string): Promise<{ 
    success: boolean; 
    content?: string; 
    document?: any; 
    error?: string 
  }> {
    try {
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success && data.document) {
        // Reconstruct full text from chunks
        const chunks = data.document.chunks || [];
        const fullContent = chunks
          .sort((a: any, b: any) => a.chunk_index - b.chunk_index)
          .map((chunk: any) => chunk.content)
          .join('\n\n');
        
        return {
          success: true,
          content: fullContent,
          document: data.document
        };
      }

      return { success: false, error: 'Document not found' };
    } catch (error) {
      console.error('Get document content error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }

  // Generate follow-up questions based on user query and AI response
  async generateFollowUpQuestions(
    userQuery: string, 
    aiResponse: string, 
    sources: any[] = []
  ): Promise<{
    success: boolean;
    followUpQuestions?: any[];
    generatedAt?: string;
    error?: string;
  }> {
    return this.request('/follow-up/generate', {
      method: 'POST',
      body: JSON.stringify({ userQuery, aiResponse, sources }),
    });
  }
}

export const apiService = new ApiService();
export default apiService;
